import axios from "axios";

const ENV_URL = process.env.REACT_APP_BACKEND_URL;
const bare = (h) => h.replace(/^www\./, "");
// Use the current origin when on the same site (www vs non-www) to avoid cross-origin redirects
const BACKEND_URL = bare(new URL(ENV_URL).hostname) === bare(window.location.hostname) ? window.location.origin : ENV_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach auth token if present (admin takes precedence, then merchant)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pp_admin_token") || localStorage.getItem("pp_merchant_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Resolve a file url (backend returns /api/... relative paths)
export const fileUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

// ---- Public ----
export const submitEnquiry = (data) => api.post("/enquiries", data).then((r) => r.data);
export const getBlogList = (category) =>
  api.get("/blog", { params: category ? { category } : {} }).then((r) => r.data);
export const getBlogCategories = () => api.get("/blog/categories").then((r) => r.data);
export const getBlogPost = (slug) => api.get(`/blog/${slug}`).then((r) => r.data);
export const submitMerchantSignup = (data) => api.post("/merchant-signups", data).then((r) => r.data);
export const uploadPublicFile = (formData) =>
  api.post("/public-files", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);

// ---- Admin auth ----
export const adminLogin = (email, password) =>
  api.post("/admin/login", { email, password }).then((r) => r.data);
export const adminMe = () => api.get("/admin/me").then((r) => r.data);
export const getStats = () => api.get("/admin/stats").then((r) => r.data);

// ---- Unified auth (admin + merchant) ----
export const login = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const merchantMe = () => api.get("/merchant/me").then((r) => r.data);

// ---- Retailer wallet & payouts ----
export const retailerWallet = () => api.get("/retailer/wallet").then((r) => r.data);
export const retailerTransactions = () => api.get("/retailer/transactions").then((r) => r.data);
export const retailerPayouts = () => api.get("/retailer/payouts").then((r) => r.data);
export const createPayout = (data) => api.post("/retailer/payouts", data).then((r) => r.data);
export const retailerPickups = () => api.get("/retailer/cash-pickups").then((r) => r.data);
export const createPickup = (data) => api.post("/retailer/cash-pickups", data).then((r) => r.data);
export const cancelPickup = (id) => api.post(`/retailer/cash-pickups/${id}/cancel`).then((r) => r.data);
export const adminPickups = () => api.get("/admin/cash-pickups").then((r) => r.data);
export const adminUpdatePickup = (id, data) => api.patch(`/admin/cash-pickups/${id}`, data).then((r) => r.data);

// ---- Admin wallet & payouts ----
export const adminRetailers = () => api.get("/admin/retailers").then((r) => r.data);
export const adminCreateRetailer = (data) => api.post("/admin/retailers", data).then((r) => r.data);
export const adminWalletLoad = (data) => api.post("/admin/wallet/load", data).then((r) => r.data);
export const adminWalletTxns = (rid) => api.get(`/admin/wallet/${rid}/transactions`).then((r) => r.data);
export const adminPayouts = () => api.get("/admin/payouts").then((r) => r.data);
export const adminUpdatePayout = (pid, data) => api.patch(`/admin/payouts/${pid}`, data).then((r) => r.data);
export const getPayoutConfig = () => api.get("/admin/payout-config").then((r) => r.data);
export const updatePayoutConfig = (data) => api.put("/admin/payout-config", data).then((r) => r.data);
export const adminProviderBalance = () => api.get("/admin/provider-balance").then((r) => r.data);
export const adminPayoutStatusCheck = (pid) => api.post(`/admin/payouts/${pid}/status-check`).then((r) => r.data);

// ---- Admin enquiries ----
export const adminEnquiries = () => api.get("/admin/enquiries").then((r) => r.data);
export const setEnquiryStatus = (id, status) => {
  const fd = new FormData();
  fd.append("status", status);
  return api.patch(`/admin/enquiries/${id}`, fd).then((r) => r.data);
};
export const deleteEnquiry = (id) => api.delete(`/admin/enquiries/${id}`).then((r) => r.data);

// ---- Admin blog ----
export const adminBlogList = () => api.get("/admin/blog").then((r) => r.data);
export const adminBlogGet = (id) => api.get(`/admin/blog/${id}`).then((r) => r.data);
export const adminBlogCreate = (data) => api.post("/admin/blog", data).then((r) => r.data);
export const adminBlogUpdate = (id, data) => api.put(`/admin/blog/${id}`, data).then((r) => r.data);
export const adminBlogDelete = (id) => api.delete(`/admin/blog/${id}`).then((r) => r.data);
export const uploadAdminFile = (formData) =>
  api.post("/files", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);

// ---- Admin signups ----
export const adminSignups = () => api.get("/admin/merchant-signups").then((r) => r.data);
export const setSignupStatus = (id, status) => {
  const fd = new FormData();
  fd.append("status", status);
  return api.patch(`/admin/merchant-signups/${id}`, fd).then((r) => r.data);
};
export const deleteSignup = (id) => api.delete(`/admin/merchant-signups/${id}`).then((r) => r.data);

// ---- Policies ----
export const getPolicies = () => api.get("/policies").then((r) => r.data);
export const getPolicy = (slug) => api.get(`/policies/${slug}`).then((r) => r.data);
export const adminPolicies = () => api.get("/admin/policies").then((r) => r.data);
export const adminPolicyGet = (id) => api.get(`/admin/policies/${id}`).then((r) => r.data);
export const adminPolicyCreate = (data) => api.post("/admin/policies", data).then((r) => r.data);
export const adminPolicyUpdate = (id, data) => api.put(`/admin/policies/${id}`, data).then((r) => r.data);
export const adminPolicyDelete = (id) => api.delete(`/admin/policies/${id}`).then((r) => r.data);

// ---- Site settings (contact info) ----
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminUpdateSettings = (data) => api.put("/admin/settings", data).then((r) => r.data);

// ---- White-label branding ----
export const getBranding = () => api.get("/branding").then((r) => r.data);
export const adminGetBranding = () => api.get("/admin/branding").then((r) => r.data);
export const adminUpdateBranding = (data) => api.put("/admin/branding", data).then((r) => r.data);

// ---- Services (CMS) ----
export const getServices = () => api.get("/services").then((r) => r.data);
export const adminServices = () => api.get("/admin/services").then((r) => r.data);
export const adminServiceGet = (id) => api.get(`/admin/services/${id}`).then((r) => r.data);
export const adminServiceCreate = (data) => api.post("/admin/services", data).then((r) => r.data);
export const adminServiceUpdate = (id, data) => api.put(`/admin/services/${id}`, data).then((r) => r.data);
export const adminServiceDelete = (id) => api.delete(`/admin/services/${id}`).then((r) => r.data);
